import { useCallback, useMemo, useState } from 'react'

import { newestRelease, releases } from '@/routes/changelog/data'

// Single source of truth: baked from build.env's APP_VERSION via the
// `__APP_VERSION__` Vite/Vitest define (mirrors `__BUILD_SHA__`). Bumping
// APP_VERSION (which tracks the newest changelog release) re-shows the welcome
// modal once. Falls back to '0.0.0' when the define is absent (e.g. an
// unconfigured tooling context).
export const CHANGELOG_VERSION =
  typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0'

/**
 * The newest release version this account has acknowledged. Per account, not
 * per browser: two people sharing a machine — an architect checking what a
 * commercial sees — each keep their own place.
 */
export const lastSeenKey = (accountId: number | string | undefined) =>
  `autoriz.changelog.lastSeen.${accountId ?? 'anon'}`

/**
 * Before the changelog was scoped per account, the flag was one boolean per
 * version and per browser. A stored one still says which release this browser
 * acknowledged, so it seeds the new record instead of re-greeting everyone
 * with every release they already closed.
 */
export const legacySeenKey = (version: string) =>
  `autoriz.changelog.seen.v${version}`

const readLastSeen = (
  accountId: number | string | undefined,
): string | null => {
  if (typeof window === 'undefined') return newestRelease().version
  try {
    const stored = window.localStorage.getItem(lastSeenKey(accountId))
    if (stored) return stored
    const legacy = releases.find((r) =>
      window.localStorage.getItem(legacySeenKey(r.version)),
    )
    return legacy ? legacy.version : null
  } catch {
    // localStorage unavailable — treat everything as seen, never auto-open
    return newestRelease().version
  }
}

/**
 * Tracks the newest changelog release `accountId` has acknowledged; `null`
 * for an account that never closed the welcome modal. The welcome modal shows
 * the releases newer than that, filtered to the account's role.
 *
 * Read *synchronously* during render rather than in an effect, so the value is
 * already right on the first render: an effect left one render claiming
 * "nothing new" — long enough for the onboarding tour, whose own effect runs
 * first as a child of the provider, to decide the welcome modal was not coming
 * and open on top of it.
 */
export const useChangelogLastSeen = (
  accountId: number | string | undefined,
) => {
  // What markSeen recorded, held in state as well so the modal stays closed
  // even when the write to localStorage is refused.
  const [acknowledged, setAcknowledged] = useState<{
    accountId: number | string | undefined
    version: string
  } | null>(null)

  const stored = useMemo(() => readLastSeen(accountId), [accountId])
  const lastSeen =
    acknowledged && acknowledged.accountId === accountId
      ? acknowledged.version
      : stored

  /** Records that the account has now seen everything up to the newest release. */
  const markSeen = useCallback(() => {
    const version = newestRelease().version
    setAcknowledged({ accountId, version })
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(lastSeenKey(accountId), version)
    } catch {
      // ignore
    }
  }, [accountId])

  return { lastSeen, markSeen }
}
